import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSeoMeta } from '@unhead/react';
import { 
  Heart, 
  ListMusic, 
  Music2,
  Plus,
  Play,
  ArrowLeft,
  Disc3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrackTile } from '@/components/TrackTile';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLibrary } from '@/hooks/useLibrary';
import { useUserPlaylists } from '@/hooks/usePlaylists';
import { useMusicTracks } from '@/hooks/useMusicTracks';
import { usePlayer } from '@/contexts/PlayerContext';
import { LoginArea } from '@/components/auth/LoginArea';

export function LibraryPage() {
  const { user } = useCurrentUser();
  const { likedTrackIds } = useLibrary();
  const { data: playlists } = useUserPlaylists();
  const { data: allTracks, isLoading } = useMusicTracks(200);
  const { setCurrentTrack, setPlaylist, currentTrack, isPlaying, togglePlay } = usePlayer();

  useSeoMeta({
    title: 'Your Library - Cove',
    description: 'Your saved tracks and playlists on Cove',
  });

  // Get liked tracks
  const likedTracks = useMemo(() => {
    if (!allTracks) return [];
    return allTracks.filter(t => likedTrackIds.includes(t.id));
  }, [allTracks, likedTrackIds]);

  const handlePlayAll = () => {
    if (likedTracks.length > 0) {
      setPlaylist(likedTracks);
      setCurrentTrack(likedTracks[0]);
    }
  };

  const handleTrackSelect = (track: typeof likedTracks[0]) => {
    setPlaylist(likedTracks);
    if (currentTrack?.id === track.id) {
      togglePlay();
    } else {
      setCurrentTrack(track);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background pb-32">
        <header className="sticky top-0 z-40 glass border-b border-white/5">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <Disc3 className="h-6 w-6 text-primary" />
                <span className="text-xl font-bold">Cove</span>
              </Link>
            </div>
            <LoginArea className="max-w-40" />
          </div>
        </header>
        <div className="container mx-auto px-6 py-20 text-center">
          <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
          <h1 className="text-2xl font-bold mb-2">Sign in to see your library</h1>
          <p className="text-muted-foreground mb-6">Save tracks and create playlists</p>
          <LoginArea className="inline-flex" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Ambient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-bold">Your Library</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-6 relative">
        <Tabs defaultValue="liked" className="w-full">
          <TabsList className="glass-subtle border border-white/10 mb-6">
            <TabsTrigger value="liked" className="data-[state=active]:bg-orange-500/20">
              <Heart className="h-4 w-4 mr-2" />
              Liked Songs
            </TabsTrigger>
            <TabsTrigger value="playlists" className="data-[state=active]:bg-orange-500/20">
              <ListMusic className="h-4 w-4 mr-2" />
              Playlists
            </TabsTrigger>
          </TabsList>

          <TabsContent value="liked">
            {/* Liked Songs Header */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <div className="flex items-center gap-6 p-6 rounded-2xl bg-gradient-to-br from-orange-500/20 to-pink-500/10 border border-orange-500/20">
                <div className="h-24 w-24 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-2xl">
                  <Heart className="h-12 w-12 text-white" fill="white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-1">Liked Songs</h2>
                  <p className="text-muted-foreground mb-4">
                    {likedTracks.length} {likedTracks.length === 1 ? 'track' : 'tracks'}
                  </p>
                  {likedTracks.length > 0 && (
                    <Button onClick={handlePlayAll} className="rounded-full bg-orange-500 hover:bg-orange-600">
                      <Play className="h-4 w-4 mr-2" fill="currentColor" />
                      Play All
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Tracks Grid */}
            {likedTracks.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {likedTracks.map((track, index) => (
                  <TrackTile
                    key={track.id}
                    track={track}
                    onPlay={() => handleTrackSelect(track)}
                    isPlaying={currentTrack?.id === track.id && isPlaying}
                    isSelected={currentTrack?.id === track.id}
                    index={index}
                    className="w-full"
                  />
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-white/20">
                <CardContent className="py-16 text-center">
                  <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium mb-2">No liked songs yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Tap the heart on any track to save it here
                  </p>
                  <Link to="/">
                    <Button variant="outline" className="rounded-full">
                      Discover Music
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="playlists">
            {playlists && playlists.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {playlists.map((playlist, index) => (
                  <motion.div
                    key={playlist.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="glass-card border-white/10 hover:border-orange-500/30 transition-all cursor-pointer group">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-16 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                            <Music2 className="h-8 w-8 text-muted-foreground group-hover:text-orange-400 transition-colors" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate group-hover:text-orange-400 transition-colors">
                              {playlist.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {playlist.trackCount} tracks
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-white/20">
                <CardContent className="py-16 text-center">
                  <ListMusic className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium mb-2">No playlists yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create a playlist to organize your music
                  </p>
                  <Button variant="outline" className="rounded-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Playlist
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
